export type PlaceCategoryOption = {
  key: string;
  label: string;
};

const CATEGORY_ALIAS_MAP: Record<string, string[]> = {
  fastfood: ["fastfood", "fast_food", "quick_service", "qsr"],
  pizza: ["pizza", "pizzeria"],
  diner: ["diner", "breakfast", "brunch", "cafe", "coffee"],
  wings: ["wings", "chicken_wings", "wing", "buffalo_wings"],
  burgers: ["burgers", "burger", "hamburger"],
  mexican: ["mexican", "taco", "taqueria"],
  chinese: ["chinese", "asian", "noodles"],
  subs: ["subs", "sub", "sandwich", "sandwiches", "hoagie"],
  ice_cream: ["ice_cream", "gelato", "frozen_yogurt"],
  donuts: ["donuts", "donut"],
  food: ["food", "restaurant", "eatery"],
  bar: ["bar", "pub", "tavern"],
  nightclub: ["nightclub", "night_club", "club"],
  liquor: ["liquor", "liquor_store", "alcohol"],
  casino: ["casino"],
  coffee: ["coffee", "cafe", "cafeteria"],
  gas: ["gas", "gas_station", "fuel", "petrol"],
  ev_charging: ["ev_charging", "ev_charger", "charging_station"],
  auto_parts: ["auto_parts", "autoparts", "car_parts"],
  car_wash: ["car_wash", "carwash"],
  towing: ["towing", "tow"],
  pharmacy: ["pharmacy", "drugstore", "chemist"],
  urgent_care: ["urgent_care", "urgentcare", "clinic"],
  hospital: ["hospital", "er", "emergency_room"],
  emergency_vet: ["emergency_vet", "vet", "veterinary", "animal_hospital"],
  convenience: ["convenience", "convenience_store", "bodega", "corner_store"],
  grocery: ["grocery", "supermarket", "market"],
  vape: ["vape", "smoke_shop", "smoke"],
  tobacco: ["tobacco", "cigar", "cigarette"],
  atm: ["atm", "bank_machine"],
  laundromat: ["laundromat", "laundry"],
  hotel: ["hotel", "motel", "inn"],
  gym: ["gym", "fitness", "health_club"],
  entertainment: ["entertainment", "arcade", "bowling", "movie_theater", "cinema"],
  printing: ["printing", "print_shop", "copy_shop"],
};

export const PLACE_CATEGORY_OPTIONS: PlaceCategoryOption[] = [
  { key: "", label: "🌙 All" },
  { key: "fastfood", label: "🍔 Fast Food" },
  { key: "pizza", label: "🍕 Pizza" },
  { key: "diner", label: "🥞 Diner / Breakfast" },
  { key: "wings", label: "🍗 Wings" },
  { key: "burgers", label: "🍟 Burgers" },
  { key: "mexican", label: "🌮 Mexican" },
  { key: "chinese", label: "🥡 Chinese / Asian" },
  { key: "subs", label: "🥪 Subs / Sandwiches" },
  { key: "ice_cream", label: "🍦 Ice Cream" },
  { key: "donuts", label: "🍩 Donuts / Bakery" },
  { key: "food", label: "🍽️ Other Food" },
  { key: "bar", label: "🍺 Bar" },
  { key: "nightclub", label: "🎉 Nightclub" },
  { key: "liquor", label: "🥃 Liquor Store" },
  { key: "casino", label: "🎰 Casino" },
  { key: "coffee", label: "☕ Coffee / Café" },
  { key: "gas", label: "⛽ Gas Station" },
  { key: "ev_charging", label: "🔋 EV Charging" },
  { key: "auto_parts", label: "🔧 Auto Parts" },
  { key: "car_wash", label: "🚗 Car Wash" },
  { key: "towing", label: "🚨 Towing" },
  { key: "pharmacy", label: "💊 Pharmacy" },
  { key: "urgent_care", label: "🏥 Urgent Care" },
  { key: "hospital", label: "🚑 ER / Hospital" },
  { key: "emergency_vet", label: "🐾 Emergency Vet" },
  { key: "convenience", label: "🛒 Convenience Store" },
  { key: "grocery", label: "🥦 Grocery" },
  { key: "vape", label: "💨 Vape / Smoke" },
  { key: "tobacco", label: "🚬 Tobacco" },
  { key: "atm", label: "🏧 ATM" },
  { key: "laundromat", label: "👕 Laundromat" },
  { key: "hotel", label: "🏨 Hotel / Motel" },
  { key: "gym", label: "💪 24hr Gym" },
  { key: "entertainment", label: "🎳 Entertainment" },
  { key: "printing", label: "🖨️ Printing" },
  { key: "__search__", label: "🔍 Search..." },
];

function normalizeCategoryValue(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
}

function matchesAlias(normalizedCategory: string, normalizedAlias: string): boolean {
  if (normalizedCategory === normalizedAlias) return true;
  if (normalizedCategory.startsWith(`${normalizedAlias}_`)) return true;
  if (normalizedCategory.endsWith(`_${normalizedAlias}`)) return true;
  return normalizedCategory.includes(`_${normalizedAlias}_`);
}

export function getCategoryFilterValues(categoryKey: string): string[] {
  const normalizedKey = normalizeCategoryValue(categoryKey);
  if (!normalizedKey || normalizedKey === "__search__") return [];

  const aliases = CATEGORY_ALIAS_MAP[normalizedKey] ?? [normalizedKey];
  const normalizedAliases = aliases.map(normalizeCategoryValue).filter(Boolean);
  return Array.from(new Set(normalizedAliases));
}

export function categoryMatchesSelection(rawCategory: string, selectedCategoryKey: string): boolean {
  const normalizedCategory = normalizeCategoryValue(rawCategory);
  const filters = getCategoryFilterValues(selectedCategoryKey);
  if (filters.length === 0) return true;
  return filters.some((filter) => matchesAlias(normalizedCategory, filter));
}
